// FairMatch — 모바일 웹 SPA 진입점.
//
// 모든 도메인 leaf는 src/ 하위 모듈로 추출돼 있고, 이 파일은 그것들을 연결하는
// 글루: 라우터에 페이지 핸들러 주입, 시트 호스트 마운트, 부팅.
//
// 페이지 wrapper들은 extracted 모듈에 `{ rerender: render, ... }` 콜백을
// 주입하기 위해 여기서 정의. extracted 모듈이 직접 라우터를 import하면 순환
// 의존이 생겨 모두 callback 기반.

// vite.config.ts의 define으로 빌드 시점 날짜 주입. UI에 노출 X — 콘솔 전용.
declare const __BUILD_DATE__: string;

import { api } from "./api";
import { isAdmin } from "./auth";
import { LAST_CLUB_KEY, loadPersisted, persist, setMembersFetcherApi, state } from "./state";
import {
  goBack, navigate, parseSpecialRoute, resolveClubSlugFromUrl, setRouterDeps,
} from "./routes";
import {
  setRouterPages,
  render,
  renderFab,
  startPoll,
  stopPoll,
} from "./routes/router";
import { renderNoClubLanding } from "./routes/noClubLanding";
import { mountSheet } from "./ui/sheetChrome";
import { closeSheet, toast } from "./ui/sheet";
import { closeKebabMenu, openKebabMenu } from "./ui/kebabMenu";

// Page modules
import { renderDashboard as renderDashboardPage } from "./dashboard";
import { renderEventsList } from "./events/eventsList";
import { renderStats } from "./stats/page";
import { renderEventDetail as renderEventDetailPage } from "./events/eventDetail";
import { renderClubSettings as renderClubSettingsPage } from "./members/clubSettings";
import { renderMembersList } from "./members/membersList";
import { renderMyRecord as renderMyRecordPage } from "./members/myRecord";
import {
  renderInviteActivate as renderInviteActivatePage,
  renderMemberLogin as renderMemberLoginPage,
  renderRosterJoin as renderRosterJoinPage,
} from "./auth/joinPages";
import { renderGuestEvent as renderGuestEventPage } from "./events/guestEventPage";

// Sheet modules
import { buildMatchCard } from "./matches/cards";
import { openScoreSheet as openScoreSheetImpl, openSwapSheet as openSwapSheetImpl } from "./matches/sheets";
import {
  openAddMatchSheet as openAddMatchSheetImpl,
  openCompleteEventSheet as openCompleteEventSheetImpl,
  openEditEventSheet as openEditEventSheetImpl,
  openGenerateScheduleSheet as openGenerateScheduleSheetImpl,
  openNewEventSheet as openNewEventSheetImpl,
} from "./events/sheets";
import { buildTemplatePickerSection, openSaveAsTemplatePromptSheet } from "./events/templates";
import {
  openAddParticipantSheet as openAddParticipantSheetPage,
  openParticipantAvailabilitySheet as openParticipantAvailabilitySheetPage,
} from "./events/participantSheets";
import { openGuestLinkSheet } from "./events/guestLinkSheet";
import {
  archiveEvent as archiveEventImpl,
  deleteEvent as deleteEventImpl,
  revertEventToReady as revertEventToReadyImpl,
  startEvent as startEventImpl,
  unarchiveEvent as unarchiveEventImpl,
} from "./events/lifecycle";
import { openProfileEditSheet as openProfileEditSheetPage } from "./members/profileEditSheet";
import {
  openBulkRegisterSheet as openBulkRegisterSheetPage,
  openEditMemberSheet as openEditMemberSheetPage,
  openNewMemberSheet as openNewMemberSheetPage,
} from "./members/memberForms";
import { openSettingsSheet as openSettingsSheetPage } from "./ui/settingsSheet";

// ---------- Cross-module wiring ----------

// state의 fetchMembers가 api를 쓸 수 있도록 주입(순환 import 회피).
setMembersFetcherApi(api);

// 라우터의 navigate가 render/toast를 호출할 수 있도록 주입.
setRouterDeps({
  render: () => render(),
  toast: (msg: string) => toast(msg),
});

// ---------- Page wrappers (extracted 모듈에 render/콜백 주입용 글루) ----------

async function renderDashboard(view: HTMLElement) {
  await renderDashboardPage({
    view,
    matchCard,
    onNewEvent: () => openNewEventSheet(),
  });
}

async function renderEvents(view: HTMLElement) {
  await renderEventsList({
    view,
    onNew: () => openNewEventSheet(),
    onKebab: (ev, anchor) => openEventKebabMenu(ev, anchor),
    renderFab,
  });
}

async function renderEventDetail(view: HTMLElement, eventId: string) {
  await renderEventDetailPage({
    view, eventId,
    openEditEventSheet,
    openAddParticipantSheet,
    openGuestLinkSheet,
    openCompleteEventSheet,
    openParticipantAvailabilitySheet,
    openGenerateScheduleSheet,
    openAddMatchSheet,
    openScoreSheet,
    openSwapSheet,
    startEvent,
    revertEventToReady,
    rerender: render,
    startPoll,
  });
}

async function renderMembers(view: HTMLElement) {
  await renderMembersList({
    view,
    onEditMember: (m) => openEditMemberSheet(m),
    onNewMember: (members) => openNewMemberSheet(members),
    onBulkRegister: (members) => openBulkRegisterSheet(members),
    renderFab,
  });
}

async function renderClubSettings(view: HTMLElement) {
  await renderClubSettingsPage(view);
}

async function renderMyRecord(view: HTMLElement) {
  await renderMyRecordPage({
    view,
    onEdit: (member, onSaved) => openProfileEditSheet(member, onSaved),
    rerender: () => { view.innerHTML = ""; renderMyRecord(view); },
  });
}

async function renderInviteActivate(view: HTMLElement, memberId: string, token: string) {
  await renderInviteActivatePage(view, memberId, token);
}

async function renderRosterJoin(view: HTMLElement, token: string) {
  await renderRosterJoinPage(view, token);
}

async function renderMemberLogin(view: HTMLElement) {
  await renderMemberLoginPage(view);
}

async function renderGuestEvent(view: HTMLElement, eventId: string, token: string) {
  await renderGuestEventPage(view, eventId, token, { rerender: render });
}

// ---------- Sheet wrappers (render/navigate를 콜백으로 주입) ----------

function matchCard(m: any, ev: any, opts?: any) {
  return buildMatchCard(m, ev, {
    ...(opts || {}),
    onScoreClick: () => openScoreSheet(m, ev),
    onPlayerSwap: (pid: string) => openSwapSheet(m, pid, ev),
  });
}

function openAddMatchSheet(ev: any, participants: any[], courts: any[], existingBonusRounds: number[], opts?: any) {
  openAddMatchSheetImpl(ev, participants, courts, existingBonusRounds, render, opts);
}

async function openSwapSheet(match: any, fromPid: string, ev: any) {
  await openSwapSheetImpl(match, fromPid, ev, async () => {
    stopPoll();
    if (state.route.name === "event") {
      const view = document.getElementById("view");
      if (view) {
        view.innerHTML = "";
        await renderEventDetail(view, ev.event_id);
      }
    }
  });
}

function openScoreSheet(match: any, ev: any) {
  openScoreSheetImpl(match, ev, render);
}

async function openEditEventSheet(ev: any, existingCourts: any[] = [], eventParticipants: any[] = []) {
  await openEditEventSheetImpl(ev, existingCourts, eventParticipants, {
    onSaved: render,
    onDeleted: () => navigate({ name: "events" }),
  });
}

function openCompleteEventSheet(ev: any, matches: any[]) {
  openCompleteEventSheetImpl(ev, matches, render);
}

function openGenerateScheduleSheet(ev: any, regenerate: boolean) {
  openGenerateScheduleSheetImpl(ev, regenerate, render);
}

async function openNewEventSheet() {
  // 템플릿 적용은 활성 클럽원 누구나 — 정기모임 생성 권한이 풀렸으니
  // 템플릿도 같이 노출해야 일관성. 단, "현재 세팅을 템플릿으로 저장" 프롬프트는
  // 운영진만 — 템플릿 마스터 관리는 클럽 운영 자산이라 write 권한 분리.
  await openNewEventSheetImpl({
    onCreated: (newEventId: string) => navigate({ name: "event", id: newEventId }),
    buildTemplatePicker: buildTemplatePickerSection,
    openSaveAsTemplatePromptSheet: isAdmin() ? openSaveAsTemplatePromptSheet : undefined,
  });
}

function openSettingsSheet() {
  openSettingsSheetPage({ rerender: render });
}

function openProfileEditSheet(member: any, onSaved?: () => void) {
  openProfileEditSheetPage(member, onSaved);
}

function openNewMemberSheet(existingMembers: any[]) {
  openNewMemberSheetPage(existingMembers, { rerender: render });
}

function openBulkRegisterSheet(existingMembers: any[]) {
  openBulkRegisterSheetPage(existingMembers, { rerender: render });
}

function openEditMemberSheet(m: any) {
  openEditMemberSheetPage(m, { rerender: render });
}

async function openParticipantAvailabilitySheet(ev: any, p: any) {
  await openParticipantAvailabilitySheetPage(ev, p, { rerender: render });
}

async function openAddParticipantSheet(
  eventId: string,
  existingParticipants: any[] = [],
  ev?: any,
  canManage?: boolean,
) {
  await openAddParticipantSheetPage(eventId, existingParticipants, { rerender: render, ev, canManage });
}

// ---------- Lifecycle wrappers ----------

const startEvent = (eventId: string, btn: HTMLButtonElement) =>
  startEventImpl(eventId, btn, { onChanged: render });
const revertEventToReady = (eventId: string, btn: HTMLButtonElement) =>
  revertEventToReadyImpl(eventId, btn, { onChanged: render });
const archiveEvent = (eventId: string, btn: HTMLButtonElement) =>
  archiveEventImpl(eventId, btn, { onChanged: render });
const unarchiveEvent = (eventId: string, btn: HTMLButtonElement) =>
  unarchiveEventImpl(eventId, btn, { onChanged: render });
const deleteEvent = (ev: any, btn: HTMLButtonElement) => deleteEventImpl(ev, btn, {
  onDeleted: () => navigate({ name: state.tab === "home" ? "home" : "events" }),
});

// ---------- Kebab menu (이벤트 카드 우상단) ----------

function openEventKebabMenu(ev: any, anchorBtn: HTMLElement) {
  const items: any[] = [];
  if (ev.status === "draft" || ev.status === "ready") {
    items.push({ label: "삭제", danger: true, onclick: (e: any) => { closeKebabMenu(); deleteEvent(ev, e.currentTarget); } });
  } else if (ev.status === "completed") {
    items.push({ label: "숨기기", onclick: (e: any) => { closeKebabMenu(); archiveEvent(ev.event_id, e.currentTarget); } });
  } else if (ev.status === "archived") {
    items.push({ label: "숨김 해제", onclick: (e: any) => { closeKebabMenu(); unarchiveEvent(ev.event_id, e.currentTarget); } });
  }
  openKebabMenu(items, anchorBtn);
}

// ---------- Router page registry ----------
// 모든 페이지 wrapper가 정의된 뒤 라우터에 등록.

setRouterPages({
  renderDashboard,
  renderEvents,
  renderStats,
  renderEventDetail,
  renderMembers,
  renderClubSettings,
  renderMemberLogin,
  renderInviteActivate,
  renderMyRecord,
  renderGuestEvent,
  renderRosterJoin,
});

// ---------- Bootstrapping ----------

function syncDocumentTitle() {
  const name = state.club?.name;
  if (name) document.title = name;
}

async function loadClub() {
  try {
    const res = await api(`/api/v1/public/clubs/${state.config.clubSlug}`);
    state.club = res?.club || res;
    persist();
    syncDocumentTitle();
  } catch (e) {
    console.warn("club load failed", e);
  }
}

function bindGlobals() {
  document.addEventListener("click", (e) => {
    const target = (e.target as Element)?.closest?.("[data-action]") as HTMLElement | null;
    if (!target) return;
    const action = target.dataset.action;
    if (action === "back") goBack();
    else if (action === "open-settings") openSettingsSheet();
    else if (action === "close-sheet") closeSheet();
  });
}

// 방문자 카운터 — fire-and-forget. localStorage로 하루 1회 제한.
// 백엔드는 IP/UA 등 PII 저장 X, 단순 카운터만 (admin_api/_handle_public_track).
// role: admin/member/guest/anon 4분기로 회원 활성도와 운영진 동선을 구분.
function trackVisit(role: "admin" | "member" | "guest" | "anon") {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const key = "fairmatch:visited";
    if (localStorage.getItem(key) === today) return;
    localStorage.setItem(key, today);
    const base = (state.config.apiBase || "").replace(/\/$/, "");
    if (!base) return;
    fetch(`${base}/api/v1/public/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        club_slug: state.config.clubSlug || null,
        actor_role: role,
      }),
    }).catch(() => {});
  } catch { /* swallow */ }
}

// 방문자 분류 — guest_event 라우트면 "guest"(token 접근), 아니면 세션의
// app_role을 admin/member로 매핑, 세션 없으면 "anon".
function visitorRole(): "admin" | "member" | "guest" | "anon" {
  if (state.route?.name === "guest_event") return "guest";
  const r = state.session?.actor?.app_role;
  if (!r) return "anon";
  if (r === "participant") return "member";
  return "admin"; // staff / admin / president / platform_admin
}

async function boot() {
  bindGlobals();
  const urlSlug = resolveClubSlugFromUrl();
  if (urlSlug) {
    state.config.clubSlug = urlSlug;
  } else {
    const lastSlug = localStorage.getItem(LAST_CLUB_KEY);
    if (lastSlug) {
      window.location.replace(`/c/${lastSlug}/`);
      return;
    }
    renderNoClubLanding();
    trackVisit("anon");
    return;
  }
  loadPersisted();
  syncDocumentTitle();
  await loadClub();

  const special = parseSpecialRoute();
  if (special.kind === "invite") {
    state.route = { name: "invite_activate", memberId: special.memberId, token: special.token };
  } else if (special.kind === "login") {
    state.route = { name: "member_login" };
  } else if (special.kind === "guest") {
    state.session = null;
    state.route = { name: "guest_event", eventId: special.eventId, token: special.token };
  } else if (special.kind === "roster_join") {
    state.session = null;
    state.route = { name: "roster_join", token: special.token };
  }
  // session/route가 모두 결정된 후에 트래킹 — 정확한 역할 분류 위해.
  trackVisit(visitorRole());
  await render();
}

// ---------- Entry ----------

// 빌드 디버그용 — 회장 등이 F12 콘솔로 어느 빌드인지 확인 가능.
console.info(`FairMatch · build ${__BUILD_DATE__}`);

const sheetRoot = document.getElementById("sheet-root");
if (sheetRoot) mountSheet(sheetRoot);

void boot();
